/**
 * Seção "Mulheres reais (UGC)" — carousel + modal de reels.
 *
 * O Embla Carousel é o motor do carousel: cuida de swipe/drag, setas
 * (scrollPrev/scrollNext), loop infinito, snap centralizado e resize
 * (ResizeObserver interno). Este módulo apenas:
 *   - garante slides suficientes para o loop preencher o viewport;
 *   - reflete o slide central (.is-center) e dá autoplay no vídeo central;
 *   - liga as setas e reflete o estado dos botões;
 *   - mantém a modal de reels (comportamento idêntico ao anterior).
 */
import EmblaCarousel from 'embla-carousel';

const prefersReducedMotion = () =>
  !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

/**
 * O loop do Embla só fica sem "buracos" quando a soma das larguras dos slides
 * ultrapassa o viewport. Como esta seção costuma ter poucos cards, clonamos o
 * conjunto o mínimo necessário para preencher — mesma estratégia da versão
 * anterior, agora só para alimentar o Embla.
 */
function ensureLoopFill(container, originals) {
  const target = Math.max(window.innerWidth, 1440) * 2;
  let guard = 0;
  while (container.scrollWidth < target && guard < 10) {
    const fragment = document.createDocumentFragment();
    originals.forEach((card) => {
      const clone = card.cloneNode(true);
      clone.dataset.clone = '1';
      clone.setAttribute('aria-hidden', 'true');
      fragment.appendChild(clone);
    });
    container.appendChild(fragment);
    guard += 1;
  }
}

function initCarousel(root) {
  if (root.dataset.helloBound === '1') return;
  const viewport = root.querySelector('.hello-ugc__track');
  const container = root.querySelector('.hello-ugc__container');
  if (!viewport || !container) return;

  const originals = Array.from(container.querySelectorAll('.hello-ugc-card'));
  if (!originals.length) return;
  root.dataset.helloBound = '1';

  const reduceMotion = prefersReducedMotion();
  let modalOpen = false;

  /* ---------- Playlist de vídeos (para a modal) — a partir dos cards originais ---------- */
  const playlist = [];
  originals.forEach((card, slideIndex) => {
    if (!card.dataset.hasVideo) return;
    card.dataset.ugcIndex = String(playlist.length);
    playlist.push({
      // Índice do slide correspondente (originais vêm antes dos clones), usado
      // para o carousel de trás acompanhar o vídeo aberto no modal.
      slideIndex: slideIndex,
      src: card.dataset.ugcVideoSrc || '',
      poster: card.dataset.ugcPoster || '',
      variant: card.dataset.ugcVariant || '',
      title: card.dataset.ugcTitle || '',
      price: card.dataset.ugcPrice || '',
      image: card.dataset.ugcImage || '',
      url: card.dataset.ugcUrl || '',
    });
  });

  // Clones herdam data-ugc-index dos originais (definido acima), então clicar
  // num clone abre o item de vídeo correto na modal.
  ensureLoopFill(container, originals);

  /* ---------- Embla: motor do carousel ---------- */
  const embla = EmblaCarousel(viewport, {
    loop: true,
    align: 'center',
    containScroll: false,
    // Arraste rápido percorre vários cards com desaceleração (em vez de parar
    // sempre no próximo), ainda ancorando no card central ao final.
    skipSnaps: true,
  });
  root._emblaApi = embla;

  /* ---------- Card central: escala (.is-center) + autoplay do vídeo ---------- */
  const syncCenter = () => {
    const selected = embla.selectedScrollSnap();
    embla.slideNodes().forEach((node, i) => {
      const isCenter = i === selected;
      node.classList.toggle('is-center', isCenter);
      const video = node.querySelector('video');
      if (!video) return;
      if (isCenter && !reduceMotion && !modalOpen) {
        const playing = video.play();
        if (playing && playing.catch) playing.catch(() => {});
      } else {
        video.pause();
      }
    });
  };

  /* ---------- Setas ---------- */
  const prevBtn = root.querySelector('[data-dir="prev"]');
  const nextBtn = root.querySelector('[data-dir="next"]');
  if (prevBtn) prevBtn.addEventListener('click', () => embla.scrollPrev());
  if (nextBtn) nextBtn.addEventListener('click', () => embla.scrollNext());

  const syncButtons = () => {
    // Com loop ativo o Embla nunca trava nas pontas; refletimos o estado real
    // mesmo assim (acessibilidade / caso o loop seja desativado no futuro).
    if (prevBtn) prevBtn.disabled = !embla.canScrollPrev();
    if (nextBtn) nextBtn.disabled = !embla.canScrollNext();
  };

  embla
    .on('select', syncCenter)
    .on('select', syncButtons)
    .on('reInit', syncCenter)
    .on('reInit', syncButtons);
  syncCenter();
  syncButtons();

  /* ---------- Modal de reels (comportamento preservado) ---------- */
  const modal = root.querySelector('.hello-ugc-modal');
  if (!modal || !playlist.length) return;

  const mVideo = modal.querySelector('[data-ugc-video]');
  const mStage = modal.querySelector('[data-ugc-stage]');
  const mSound = modal.querySelector('[data-ugc-sound]');
  const mClose = modal.querySelector('[data-ugc-close]');
  const mNavs = Array.from(modal.querySelectorAll('[data-ugc-nav]'));
  const mImg = modal.querySelector('[data-ugc-p-image]');
  const mTitle = modal.querySelector('[data-ugc-p-title]');
  const mPrice = modal.querySelector('[data-ugc-p-price]');
  const mAdd = modal.querySelector('[data-ugc-add]');
  const mProduct = modal.querySelector('.hello-ugc-modal__product');
  let current = 0;

  const updateSoundIcon = () => {
    if (mSound && mVideo) mSound.setAttribute('data-muted', mVideo.muted ? '1' : '0');
  };

  const renderItem = () => {
    const item = playlist[current];
    if (!item) return;
    // Mantém o carousel de trás centralizado no vídeo aberto no modal (pulo
    // instantâneo, pois o modal cobre a tela — ao fechar já está no card certo).
    if (typeof item.slideIndex === 'number') embla.scrollTo(item.slideIndex, true);
    if (mVideo) {
      mVideo.src = item.src;
      mVideo.muted = false;
      try { mVideo.currentTime = 0; } catch (e) { /* noop */ }
      const playing = mVideo.play();
      if (playing && playing.catch) playing.catch(() => {});
    }
    updateSoundIcon();
    // Sem produto associado ao card → esconde a barra do produto inteira.
    const hasProduct = item.variant || item.title;
    if (mProduct) mProduct.style.display = hasProduct ? '' : 'none';
    if (mImg) {
      if (item.image) { mImg.src = item.image; mImg.style.display = ''; }
      else { mImg.removeAttribute('src'); mImg.style.display = 'none'; }
    }
    if (mTitle) mTitle.textContent = item.title;
    if (mPrice) mPrice.innerHTML = item.price;
    if (mAdd) {
      if (item.variant) {
        mAdd.setAttribute('data-variant', item.variant);
        mAdd.style.display = '';
        mAdd.disabled = false;
        mAdd.textContent = 'Adicionar ao carrinho';
      } else {
        mAdd.style.display = 'none';
      }
    }
    const multi = playlist.length > 1;
    mNavs.forEach((b) => { b.style.display = multi ? '' : 'none'; });
  };

  const openModal = (i) => {
    current = ((i % playlist.length) + playlist.length) % playlist.length;
    modalOpen = true;
    container.querySelectorAll('video').forEach((v) => v.pause());
    renderItem();
    if (modal.showModal) { try { modal.showModal(); } catch (e) { modal.setAttribute('open', ''); } }
    else modal.setAttribute('open', '');
  };

  const closeModal = () => {
    if (mVideo) { mVideo.pause(); mVideo.removeAttribute('src'); mVideo.load(); }
    if (modal.open && modal.close) modal.close();
    else modal.removeAttribute('open');
  };

  const nav = (dir) => {
    current = (((current + dir) % playlist.length) + playlist.length) % playlist.length;
    renderItem();
  };

  // Abre a modal ao clicar num card de vídeo. O Embla já suprime o clique após
  // arrastar, então não precisamos de lógica manual de "dragMoved".
  container.addEventListener('click', (e) => {
    const card = e.target.closest('.hello-ugc-card--video');
    if (!card || !container.contains(card)) return;
    const idx = parseInt(card.dataset.ugcIndex, 10);
    if (Number.isNaN(idx)) return;
    e.preventDefault();
    openModal(idx);
  });

  if (mVideo) mVideo.addEventListener('click', () => {
    if (mVideo.paused) { const p = mVideo.play(); if (p && p.catch) p.catch(() => {}); }
    else mVideo.pause();
  });
  if (mSound) mSound.addEventListener('click', () => {
    mVideo.muted = !mVideo.muted;
    updateSoundIcon();
  });
  mNavs.forEach((b) => {
    b.addEventListener('click', () => nav(parseInt(b.getAttribute('data-ugc-nav'), 10) || 1));
  });
  if (mClose) mClose.addEventListener('click', closeModal);
  modal.addEventListener('click', (e) => { if (e.target === modal) closeModal(); });
  modal.addEventListener('close', () => {
    modalOpen = false;
    if (mVideo) mVideo.pause();
    syncCenter();
  });

  if (mStage) {
    let touchX = null;
    mStage.addEventListener('touchstart', (e) => { touchX = e.touches[0].clientX; }, { passive: true });
    mStage.addEventListener('touchend', (e) => {
      if (touchX === null) return;
      const dx = e.changedTouches[0].clientX - touchX;
      touchX = null;
      if (Math.abs(dx) > 50) nav(dx < 0 ? 1 : -1);
    }, { passive: true });
  }

  if (mAdd) mAdd.addEventListener('click', () => {
    const variant = mAdd.getAttribute('data-variant');
    if (!variant) return;
    const addUrl = (window.Theme && Theme.routes && Theme.routes.cart_add_url) || '/cart/add.js';
    const cartUrl = (window.Theme && Theme.routes && Theme.routes.cart_url) || '/cart';
    mAdd.disabled = true;
    mAdd.textContent = 'Adicionando...';
    fetch(addUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: variant, quantity: 1 }),
    })
      .then((r) => { if (!r.ok) throw new Error('add'); return r.json(); })
      .then(() => { window.location.href = cartUrl; })
      .catch(() => { mAdd.disabled = false; mAdd.textContent = 'Adicionar ao carrinho'; });
  });
}

function destroyCarousel(root) {
  if (root._emblaApi) { root._emblaApi.destroy(); root._emblaApi = null; }
  root.dataset.helloBound = '';
}

const initAll = (scope) => {
  (scope || document).querySelectorAll('.hello-ugc').forEach(initCarousel);
};

if (document.readyState !== 'loading') initAll();
else document.addEventListener('DOMContentLoaded', () => initAll());

// Editor de temas da Shopify: reinicializa / limpa ao adicionar/remover a seção.
document.addEventListener('shopify:section:load', (e) => initAll(e.target));
document.addEventListener('shopify:section:unload', (e) => {
  e.target.querySelectorAll('.hello-ugc').forEach(destroyCarousel);
});
